<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRoute } from 'vue-router'

interface MenuItem {
  name: string
  title: string
  route: string
  description: string
  buttonText: string
}

const route = useRoute()
const isCollapsed = ref(false)
const userName = ref('Admin User')

const menuItems = ref<MenuItem[]>([
  {
    name: 'Medical Schemes',
    title: 'Active Medical Schemes',
    route: '/health-insurances',
    description: 'This is the list of active medical schemes partners.',
    buttonText: 'MEDICAL SCHEMES',
    // icon: <IconName />
  },
  {
    name: 'Tariff Rates Management',
    title: 'Tariff Rates Management',
    route: '/schemes-tariff-rates',
    description: 'Manage Tariff Codes. Tariff codes are used to define the services and procedures that can be billed to clients. Amendment differs per business need.',
    buttonText: 'MANAGE TARIFF CODES AMENDMENT'
  },
  {
    name: 'Rollover Management',
    title: 'Tariff Code Rollover Staging',
    route: '/rollover-staging',
    description: 'Stage tariff code rollovers for the upcoming year. This allows you to prepare and review tariff code changes before they are applied.',
    buttonText: 'STAGE TARIFF CODES ROLLOVER'
  },
  {
    name: 'Reports',
    title: 'Reports',
    route: '/report-types',
    description: 'Report Types List',  
    buttonText: 'VIEW REPORTS'
  },
  // {
  //   name: 'AD-HOC',
  //   title: 'AD-HOC',
  //   route: '/programmes',
  //   description: 'Programme List',  
  //   buttonText: 'AD-HOC'
  // },
  // {
  //   name: 'Allowed Quantities',
  //   title: 'Tariff Code Allowed Quantities',
  //   route: '/tariff-codes',
  //   description: 'Manage allowed quantities for tariff codes. This is used to set the maximum number of units that can be billed for a tariff code.',
  //   buttonText: 'MANAGE ALLOWED QUANTITIES' 
  // },
])

const userInitials = computed(() => {
  return userName.value
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
})

const toggleSidebar = () => {
  isCollapsed.value = !isCollapsed.value
}

const isActive = (itemRoute: string) => {
  return route.path === itemRoute
}

const handleNavClick = (item: MenuItem) => {
  console.log('Navigating to:', item.name)
}
</script>

<template>
  <div class="sidebar-container">
    <!-- Sidebar -->
    <aside 
      class="sidebar"
      :class="{ 'sidebar-collapsed': isCollapsed }"
    >
      <!-- Header -->
      <div class="sidebar-header">
        <div class="logo">
          <span v-if="!isCollapsed" class="logo-text">CMS Admin</span>
          <!-- <span v-else class="logo-icon">C</span> -->
        </div>
        <button 
          @click="toggleSidebar" 
          class="toggle-btn"
          aria-label="Toggle sidebar"
        >
          <svg 
            v-if="!isCollapsed"
            xmlns="http://www.w3.org/2000/svg" 
            width="20" 
            height="20" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            stroke-width="2"
          >
            <path d="M15 18l-6-6 6-6"/>
          </svg>
          <svg 
            v-else
            xmlns="http://www.w3.org/2000/svg" 
            width="20" 
            height="20" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            stroke-width="2"
          >
            <path d="M9 18l6-6-6-6"/>
          </svg>
        </button>
      </div>

      <!-- Navigation -->
      <nav class="sidebar-nav">
        <ul class="nav-list">
          <li 
            v-for="item in menuItems" 
            :key="item.name"
            class="nav-item"
          >
            <router-link
              :to="item.route"
              class="nav-link"
              :class="{ 'active': isActive(item.route) }"
              @click="handleNavClick(item)"
            >
              <div class="nav-icon">
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="20" 
                  height="20" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  stroke-width="2"
                >
                  <rect x="3" y="3" width="7" height="7"/>
                  <rect x="14" y="3" width="7" height="7"/>
                  <rect x="14" y="14" width="7" height="7"/>
                  <rect x="3" y="14" width="7" height="7"/>
                </svg>
              </div>
              <span v-if="!isCollapsed" class="nav-text">
                {{ item.name }}
              </span>
            </router-link>

            <!-- Tooltip for collapsed state -->
            <div 
              v-if="isCollapsed" 
              class="nav-tooltip"
            >
              {{ item.name }}
            </div>
          </li>
        </ul>
      </nav>

      <!-- Footer -->
      <div class="sidebar-footer">
        <div class="user-info">
          <div class="user-avatar">
            <span>{{ userInitials }}</span>
          </div>
          <div v-if="!isCollapsed" class="user-details">
            <p class="user-name">{{ userName }}</p>
            <p class="user-role">Administrator</p>
          </div>
        </div>
      </div>
    </aside>
    <!-- Main Content Area -->
    <main class="main-content">
      <slot></slot>
    </main>
  </div>
</template>

<style scoped>
.sidebar-container {
  display: flex;
  height: 100vh;
  overflow: hidden;
}

.sidebar {
  width: 280px;
  background: linear-gradient(180deg, #24637e 0%, #24637e 100%);
  color: #e2e8f0;
  display: flex;
  flex-direction: column;
  transition: width 0.3s ease;
  position: relative;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
}

.sidebar-collapsed {
  width: 80px;
}

.sidebar-header {
  padding: 1.5rem 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.logo {
  font-size: 1.5rem;
  font-weight: 700;
  color: #60a5fa;
}

.logo-text {
  display: inline-block;
  color: white;
}

.logo-icon {
  display: inline-block;
  width: 40px;
  height: 40px;
  background: #163b4c;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 1px 0 0;
  color: #0f172a;
}

.toggle-btn {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #e2e8f0;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.toggle-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.3);
}

.sidebar-nav {
  flex: 1;
  overflow-y: auto;
  padding: 1rem 0;
}

.sidebar-nav::-webkit-scrollbar {
  width: 6px;
}

.sidebar-nav::-webkit-scrollbar-track {
  background: transparent;
}

.sidebar-nav::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
}

.nav-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.nav-item {
  position: relative;
  margin: 0.25rem 0.75rem;
}

.nav-link {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  color: #cbd5e1;
  text-decoration: none;
  border-radius: 8px;
  transition: all 0.2s;
  position: relative;
}

.nav-link:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}

.nav-link.active {
  background: rgba(96, 165, 250, 0.2);
  background: #b8bda7;
  color: #316374;
}

.nav-link.active::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 60%;
  background: #b69831;
  border-radius: 0 2px 2px 0;
}

.nav-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.nav-text {
  font-size: 0.9rem;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar-collapsed .nav-link {
  justify-content: center;
  padding: 0.875rem;
}

.nav-tooltip {
  position: absolute;
  left: 100%;
  top: 50%;
  transform: translateY(-50%);
  background: #334155;
  color: #fff;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  font-size: 0.875rem;
  white-space: nowrap;
  margin-left: 0.75rem;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s;
  z-index: 1000;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
}

.nav-tooltip::before {
  content: '';
  position: absolute;
  right: 100%;
  top: 50%;
  transform: translateY(-50%);
  border: 6px solid transparent;
  border-right-color: #334155;
}

.sidebar-collapsed .nav-item:hover .nav-tooltip {
  opacity: 1;
}

.sidebar-footer {
  padding: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.user-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
}

.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #b69831, #5b4c19);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.875rem;
  flex-shrink: 0;
}

.user-details {
  flex: 1;
  min-width: 0;
}

.user-name {
  font-size: 0.875rem;
  font-weight: 600;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-role {
  font-size: 0.75rem;
  color: #94a3b8;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar-collapsed .user-info {
  justify-content: center;
}

.sidebar-collapsed .user-details {
  display: none;
}

.main-content {
  flex: 1;
  overflow-y: auto;
  background: #f8fafc;
  padding: 2rem;
}

@media (max-width: 768px) {
  .sidebar {
    position: fixed;
    left: 0;
    top: 0;
    z-index: 1000;
    transform: translateX(-100%);
  }

  .sidebar.sidebar-open {
    transform: translateX(0);
  }

  .main-content {
    margin-left: 0;
  }
}
</style>